import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import HelpModal from '../HelpModal';
import { FaPlay, FaPause, FaVolumeUp, FaVolumeMute } from 'react-icons/fa';
import HEXASLogo from '../assets/Logos/HEXASLogo.jpg';
import { useTextHighlight } from '../utils/highlightUtils';

const Class11TablePractice02 = () => {
  const navigate = useNavigate();

  const highlightRef = useRef(null);
  useTextHighlight(highlightRef);

  const [showHelp, setShowHelp] = useState(false);

  const questionRefs = useRef([]);
  const rightColRef = useRef(null);

  // Make sure your answers state exists
  const [answers, setAnswers] = useState(Array(20).fill(''));
  const [currentQuestion, setCurrentQuestion] = useState(14);
  const onNext = () => {
    setCurrentQuestion(prev => Math.min(prev + 1, 19));
  };

  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);

  // Timer state
  const [timeRemaining, setTimeRemaining] = useState(100 * 60); // 30 minutes in seconds
  const [showSeconds, setShowSeconds] = useState(false);

  // Timer effect
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 0) {
          clearInterval(timer);
          navigate('/');  // Redirect to home
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate]);

  // Format time function
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;

    if (showSeconds) {
      return `${mins} : ${secs.toString().padStart(2, '0')} remaining`;
    }
    return `${mins} minutes remaining`;
  };

  const handleStartTest = () => {
    navigate(`/guided-practice-01-form`);
  };

  const handlePlayPause = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    setCurrentTime(audioRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    if (!audioRef.current) return;
    setDuration(audioRef.current.duration);
  };

  const handleSeek = (e) => {
    if (!audioRef.current) return;
    const time = parseFloat(e.target.value);
    audioRef.current.currentTime = time;
    setCurrentTime(time);
  };

  const handleVolumeChange = (e) => {
    if (!audioRef.current) return;
    const vol = parseFloat(e.target.value);
    setVolume(vol);
    audioRef.current.volume = vol;
    setIsMuted(vol === 0);
  };

  const handleMute = () => {
    if (!audioRef.current) return;
    if (isMuted) {
      audioRef.current.muted = false;
      setIsMuted(false);
      audioRef.current.volume = volume;
    } else {
      audioRef.current.muted = true;
      setIsMuted(true);
    }
  };

  const formatAudioTime = (t) => {
    if (isNaN(t)) return '00:00';
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const GreenCheck = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="inline-block"
    >
      <path
        d="M7 11L9 13.5L16 7"
        stroke="#7DD77D"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );


  return (
    <div className="min-h-screen bg-white flex flex-col font-[Arial]">
      {/* Help Modal */}
      <HelpModal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
      />

      {/* Top Bar (fixed) */}
      <div className="sticky top-0 z-30 bg-white">
        <div className="relative h-[62px] bg-[#F4C3C3] border-b border-black flex items-center justify-between px-4">
          {/* Left + Center combined on left side */}
          <div className="flex flex-col justify-center space-y-0.5 pl-6">
            <span className="text-[14px] text-left text-black font-bold leading-tight">
              HEXA'S Education
            </span>
            <div className="flex items-center space-x-6 text-[14px] text-black font-medium">
              <span
                onMouseEnter={() => setShowSeconds(true)}
                onMouseLeave={() => setShowSeconds(false)}
                className="cursor-pointer"
              >
                {formatTime(timeRemaining)}
              </span>
            </div>
          </div>

          {/* Right: Icons and Finish Button */}
          <div className="flex items-center space-x-5">
            <button
              onClick={() => navigate('/')}
              className="text-black border border-black rounded-md px-4 py-1 text-[14px]  hover:bg-gray-100"
            >
              Back Home
            </button>
            <button
              onClick={() => setShowHelp(true)}
              className="text-black border border-black rounded-md px-4 py-1 text-[14px]  hover:bg-gray-100"
            >
              Finish test
            </button>

            {/* Icons container with larger size and spacing */}
            <div className="flex items-center space-x-8 pl-2 pr-2">
              {/* WiFi Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-9 h-9 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M17.778 8.222c-4.296-4.296-11.26-4.296-15.556 0A1 1 0 010.808 6.808c5.076-5.077 13.308-5.077 18.384 0a1 1 0 01-1.414 1.414zM14.95 11.05a7 7 0 00-9.9 0 1 1 0 01-1.414-1.414 9 9 0 0112.728 0 1 1 0 01-1.414 1.414zM12.12 13.88a3 3 0 00-4.24 0 1 1 0 01-1.415-1.415 5 5 0 017.07 0 1 1 0 01-1.415 1.415zM9 16a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>

              {/* Notification Bell Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                </svg>
              </div>

              {/* Hamburger Menu Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>

              {/* Pen/Edit Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
        {/* Title Box (fixed under top bar) */}
        <div className="relative bg-[#F4F7FA] shadow-sm border border-black rounded-lg mx-12 mt-4 mb-0 sticky top-[62px] z-20 flex items-center justify-between">
          <div className="w-full px-6 py-4 text-left">
            <h1 className="text-[16px] font-semibold text-black">
              Practice 02		Early Telecommunication Devices (HEXA’S Coursebook)
            </h1>
          </div>
        </div>
      </div>

      {/* Main content (scrollable) */}
      <div className="flex-1 flex flex-col items-center justify-start w-full bg-[#D7DFEC]">
        {/* Two-Column Main Content Area */}
        <div ref={highlightRef}
          className="flex flex-row w-full overflow-hidden -mb-16 mt-0 px-2 pb-20"
          style={{ height: 'calc(100vh - 104px)' }}
        >
          {/* Left: Reading Passage */}
          <div className="bg-[#F4F7FA] rounded-md shadow border border-gray-300 p-6 w-[49%] h-full max-h-full overflow-y-auto">
            <div className="mb-8">
              <h2 className="text-[16px] font-bold mb-4 text-center">Early Telecommunication Devices</h2>
              <h3 className="text-[14px] font-semibold mb-6 text-center italic">
                Although it is hardly used anymore, the telegraph is familiar to most people.
              </h3>

              {/* Remaining paragraphs - left aligned */}
              <div className="text-[14px] space-y-4 text-left">
                <p>
                  This early telecommunication devices are credited, as any school student knows, to Samuel Morse, who, in 1844 made the first long-distance electronic communication via his invention, the Morse's telegraph. What is not so commonly known is that Morse's was not the only telegraph nor he the only such inventor at this time. A rival system, developed by William Cook and Charles Wheatstone, was patented in England in 1845 and was subsequently adopted for use by British rail companies to enable speedy communication between rail stations.
                </p>
                <p>
                  However, the Cooke-Wheatstone telegraph, which used six wires and a fragile receiver requiring five magnetic needles, proved to the awkward to use, difficult to transport and expensive to build. Morse's version used one wire and a receiver of a simpler and stronger design. This is, no doubt, why it became the favoured telegraph in many parts of the world, especially the United States, which built a telegraph line along railway tracks crossing the North American continent, linking eastern cities with western frontiers.
                </p>
                <p>
                  Morse chose the Magnetic Telegraph Company to handle the patents for his telegraph technology, and within seven years of the appearance of his invention, the company had licensed use of the telegraph to more than 50 companies across the US. In 1851, twelve of this companies come together to form the Western Union Company. By 1866, Western Union had grown to include more than 4000 telegraph offices, almost all in rail stations.
                </p>
                <p>
                  Another early telecommunications device is still very much with us the telephone. Although the telephone is popularly thought to be the brainchild of one man, Alexander Graham Bell, this is not the whole truth. Phillip Reis, a schoolteacher in Germany, invented a device in 1861 that he labelled a telephone. Reis’s invention was limited to transmitting musical tones, however, and could not send the sound of the human voice across the wire.
                </p>
                <p>
                  While Reis was working on his invention, Bell and another man, Elisha Gray, were also working toward the invention of the telephone, though by an indirect route. Both were, in fact, seeking ways of allowing multiple telegraph signals to travel along some telegraph line - a system known as a harmonic telegraph. Bell worked in Boston while Gray was based in Chicago, and the tow were rivals in their area of research. for both inventors, the perfection of the harmonic telegraph proved too difficult and both, separately but at around the sometime, changed plans and started on the development of a telephone. Most interesting of all is the fact that both men applied for a patent to the US patent office for their respective telephones on the same day, 14 February 1876. Bell was lucky enough to have arrived a few hours earlier than Gray and so it was Bell whose name was to be forever associated with the telephone. The harmonic telegraph, incidentally, was perfected by Thomas Edison, best known as the inventor of the light built, in 1881.
                </p>
                <p>
                  Rights to Bell’s patent (now recognized as the most valuable patent in history of technology) were offered to Western Union for $100000, with the assumption that the giant telegraph company would be enthusiastic about the new technology. But Western Union disliked Bell's design and instead asked Elisha Gray to make refinements to his original telephone design. Bell`s company began to set up its own business and sell telephones, while Western Union, with its somewhat different design, was its competitor.
                </p>
                <p>
                  Competition between the two continued for about two years, but all the while, the Bell company was mounting a legal challenge to Western Union, claiming it held the only true basic patents for the telephone. it based its claim on the fact that Bell had beaten Gray to the patent office and so should be the sole recognized inventor of the telephone. Eventually, Western Union had to agree with Bell and gave up its telephone rights and patents to the Bell company. The telephone company's entire network of telephones was handed over to the Bell company. As compensation, Western Union was given 20 per cent of revenue from rental of its former equipment, this arrangement was to last until Bell's patents expired. In an effort to fight the power the Bell company enjoyed from exclusive rights to Bell's patents, a small telephone company, Pacific Union, established telephone services in the 1920s and 1930s that it claimed were based on the telephone design of Phillips Reis. They maintained that because Reis's invention pre-dated Bell1s, the Bell, design was not the first of its kind and, therefore, Bell's patents were not valid. Although the court accepted that the company may have been using Reis's technology, it nonetheless held that only Bell's patents could legally by used.
                </p>
                <p>
                  The Bell company, eventually named American Telephone & Telegraph, thus formed an effective monopoly on telephone services in the United States. The company subsequently grew to such an extent that, a century later, it was the largest privately held enterprise in the world, with more than a million employees controlling communication between more than 100 million telephones. In 1984, American Telephone & Telegraph was found by a US court to be too monopolistic and was ordered to be broken up into several smaller companies.
                </p>
              </div>
            </div>
          </div>





          {/* Right: Questions */}
          <div
            className="w-[50%] flex flex-col items-start bg-[#F4F7FA] rounded-md shadow border border-gray-300 p-6 h-full max-h-full overflow-y-auto relative"
            style={{ overflowAnchor: 'none' }}
          >
            {/* Questions Header */}
            <div className="mb-6 w-full">
              <h3 className="text-[15px] font-bold mb-3 text-left">
                Questions 1 – 5
              </h3>
              <p className="text-[14px] mb-4 text-left">
                Complete the table below.<br />
                Use no more than three words from the reading passage for each answer.
              </p>
            </div>

            {/* Table */}
            <table className="w-full border border-gray-300 text-[14px] text-left">
              <thead>
                <tr className="bg-gray-100 border-b border-gray-300">
                  <th className="p-2 border-r border-gray-300 w-[20%]">Year</th>
                  <th className="p-2 border-r border-gray-300 w-[50%]">Event</th>
                  <th className="p-2 border-gray-300 w-[30%]">Inventor(s) (by surname)</th>
                </tr>
              </thead>
              <tbody>
                {/* Row 1 */}
                <tr className="border-t border-gray-300 align-top">
                  <td className="p-2 border-r border-gray-300 ">1845</td>
                  <td className="p-2 border-r border-gray-300">Patent of telegraph</td>
                  <td className="p-2">
                    <input
                      ref={(el) => (questionRefs.current[0] = el)}
                      type="text"
                      placeholder="1"
                      className="inline-block w-full rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-1 shadow text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                      value={answers[0] || ""}
                      onChange={(e) => {
                        const newAnswers = [...answers];
                        newAnswers[0] = e.target.value;
                        setAnswers(newAnswers);
                      }}
                    />
                  </td>
                </tr>

                {/* Row 2 */}
                <tr className="border-t border-gray-300 align-top">
                  <td className="p-2 border-r border-gray-300 ">1851</td>
                  <td className="p-2 border-r border-gray-300">
                    Establishment of&nbsp;
                    <input
                      ref={(el) => (questionRefs.current[1] = el)}
                      type="text"
                      placeholder="2"
                      className="inline-block rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-1 shadow text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                      style={{ minWidth: 100 }}
                      value={answers[1] || ""}
                      onChange={(e) => {
                        const newAnswers = [...answers];
                        newAnswers[1] = e.target.value;
                        setAnswers(newAnswers);
                      }}
                    />
                  </td>
                  <td className="p-2"></td>
                </tr>

                {/* Row 3 */}
                <tr className="border-t border-gray-300 align-top">
                  <td className="p-2 border-r border-gray-300 ">1861</td>
                  <td className="p-2 border-r border-gray-300">Invention of telephone</td>
                  <td className="p-2">
                    <input
                      ref={(el) => (questionRefs.current[2] = el)}
                      type="text"
                      placeholder="3"
                      className="inline-block w-full rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-1 shadow text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                      value={answers[2] || ""}
                      onChange={(e) => {
                        const newAnswers = [...answers];
                        newAnswers[2] = e.target.value;
                        setAnswers(newAnswers);
                      }}
                    />
                  </td>
                </tr>

                {/* Row 4 */}
                <tr className="border-t border-gray-300 align-top">
                  <td className="p-2 border-r border-gray-300 ">1876</td>
                  <td className="p-2 border-r border-gray-300">
                    Application for patent of&nbsp;
                    <input
                      ref={(el) => (questionRefs.current[3] = el)}
                      type="text"
                      placeholder="4"
                      className="inline-block rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-1 shadow text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                      style={{ minWidth: 100 }}
                      value={answers[3] || ""}
                      onChange={(e) => {
                        const newAnswers = [...answers];
                        newAnswers[3] = e.target.value;
                        setAnswers(newAnswers);
                      }}
                    />
                  </td>
                  <td className="p-2">Gray</td>
                </tr>

                {/* Row 5 */}
                <tr className="border-t border-gray-300 align-top">
                  <td className="p-2 border-r border-gray-300 ">1881</td>
                  <td className="p-2 border-r border-gray-300">Successful development of&nbsp;
                    <input
                      ref={(el) => (questionRefs.current[4] = el)}
                      type="text"
                      placeholder="5"
                      className="inline-block rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-1 shadow text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                      style={{ minWidth: 100 }}
                      value={answers[4] || ""}
                      onChange={(e) => {
                        const newAnswers = [...answers];
                        newAnswers[4] = e.target.value;
                        setAnswers(newAnswers);
                      }}
                    />
                  </td>
                  <td className="p-2 ">Edison</td>
                </tr>
              </tbody>
            </table>
          </div>








        </div>
      </div>

      {/* Bottom Navigation (fixed footer) */}
      <div className="sticky bottom-0 left-0 w-full bg-white border-t border-gray-300 z-30 flex items-center justify-start px-4 py-3">
        {/* New Navigation Bar */}
        <div className="flex items-center max-w-2xl">
          {/* Part Label with bar above */}
          <div className="flex flex-col items-center mr-4">
            <div className="h-1 w-12 mb-1 rounded-full bg-gray-300" />
            <span className="text-[15px] font-bold text-gray-700">Part 1</span>
          </div>

          {/* Numbers with lines above */}
          <div className="flex items-end space-x-2">
            {[1, 2, 3, 4, 5].map((num) => {
              const isActive = currentQuestion === num;
              const isAnswered = answers[num - 1] && answers[num - 1] !== '';
              return (
                <div key={num} className="flex flex-col items-center">
                  {/* Line above number */}
                  <div
                    className="h-1 w-7 mb-1 rounded-full"
                    style={{ backgroundColor: isAnswered ? '#C9A842' : '#D1D5DB' }}
                  ></div>

                  {/* Number button */}
                  <button
                    onClick={() => {
                      setCurrentQuestion(num);
                      setTimeout(() => {
                        const input = questionRefs.current[num - 1];
                        if (input) {
                          input.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          input.focus();
                        }
                      }, 50); // tiny delay for DOM update
                    }}
                    className={`w-7 h-7 flex items-center justify-center text-[15px] font-bold
                ${isActive ? 'border-2 border-sky-400 bg-white text-sky-700 rounded-md' : 'text-gray-700'}
                transition-colors duration-150`}
                    style={{ boxShadow: isActive ? '0 0 0 0px #38bdf8 inset' : 'none' }}
                  >
                    {num}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>








    </div>
  );
};

export default Class11TablePractice02;
